<template>
  <section data-testid="offers-revamp">
    <template v-if="isLoading">
      <OffersRevampSkeleton />
    </template>
    <template v-else>
      <section class="featured-slider-wrapper bg-light-gray" v-if="!isLoading && !isMobile">
        <OffersSlider
          :isMobile="isMobile"
          :offers="slides.featuredOffers.offers.offers"
          :title="slides.featuredOffers.title"
          :total-deals="Number(slides.featuredOffers.offers.total)"
          @seeAll="handleSeeAll"
          :filters="slides.featuredOffers.filters"
          is-featured-deals
          :slides-per-view="3"
          v-if="slides.featuredOffers.offers.offers.length > 0"
        />
      </section>
      <div ref="offersRevamp">
        <OffersHeader
          @openMoreFilters="toggleMoreFilters(true)"
          @clearFilters="handleClearFilters"
        />
      </div>
      <div class="collapse-group filtered-results">
        <div v-show="showGrid" class="container" data-testid="offers-revamp-grid">
          <OffersGrid
            :title="gridTitle"
            :offers="results"
            :has-more-offers="slicedResults.length < total"
            :total-offers="total"
            :isMobile="isMobile"
            @getMoreOffers="nextPage"
          />
        </div>
        <div v-show="!results.length && !isLoading" class="container">
          <NoFilterResults @clearFilters="handleClearFilters" />
        </div>
      </div>
      <section
        class="featured-slider-wrapper bg-light-gray"
        v-if="!isLoading && isMobile && !showGrid"
      >
        <OffersSlider
          :isMobile="isMobile"
          :offers="slides.featuredOffers.offers.offers"
          :title="slides.featuredOffers.title"
          :total-deals="Number(slides.featuredOffers.offers.total)"
          :filters="slides.featuredOffers.filters"
          @seeAll="handleSeeAll"
          is-featured-deals
          :slides-per-view="3"
          v-if="slides.featuredOffers.offers.offers.length > 0"
        />
      </section>
      <div v-if="filter.isEmpty() && !isSeeAllFilter" data-testid="offers-revamp-slides">
        <template v-for="(slide, index) in slides.offers">
          <OffersSlider
            :isMobile="isMobile"
            :offers="slide.offers.offers"
            :title="slide.title"
            :total-deals="Number(slide.offers.total)"
            :key="index"
            :data-slider-id="slide.identifier"
            v-if="slide.offers.offers.length > 0"
            :filters="slide.filters"
            @seeAll="handleSeeAll"
          />
        </template>
      </div>

      <MoreFilter
        :is-open="moreFilterIsOpen"
        :points="filter.isRewardsOnly"
        :brands="facets.brands"
        @filter-change="updateMoreFilters"
        @close="toggleMoreFilters(false)"
        :isMobile="isMobile"
      ></MoreFilter>
    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { numberOfOffers, MAX_STAY } from '@/infrastructure/Configuration'
import { DateFactory, translate } from 'rhg-vue-common'
import OffersHeader from '@/components/offers/OffersHeader'
import OffersSlider from '@/components/offers/OffersSlider'
import OffersGrid from '@/components/offers/OffersGrid'
import MoreFilter from '@/components/filters/MoreFilter'
import NoFilterResults from '@/components/offers/NoFilterResults'
import OffersRevampSkeleton from '@/components/offers/OffersRevampSkeleton'
import { media, scrollEffect, getParams, removeParams } from '@/infrastructure/Utils'
import { maxLengthStayErrorModal } from '@/helpers/modalMaxStay'

export default {
  name: 'OffersRevamp',
  components: {
    OffersHeader,
    OffersSlider,
    OffersGrid,
    MoreFilter,
    NoFilterResults,
    OffersRevampSkeleton
  },
  data: () => ({
    moreFilterIsOpen: false,
    grid: {
      data: [],
      title: ''
    },
    isLoading: false,
    isMobile: false
  }),
  computed: {
    ...mapGetters('newOffers', [
      'total',
      'facets',
      'filter',
      'results',
      'slides',
      'isSeeAllFilter',
      'emptySliders',
      'locationOrRegion'
    ]),
    slicedResults() {
      return this.results.slice(0, numberOfOffers * (this.filter.offset + 1))
    },
    selectedDates() {
      return {
        startDate: this.filter.startDate ? DateFactory.formatDate(this.filter.startDate) : '',
        endDate: this.filter.endDate ? DateFactory.formatDate(this.filter.endDate) : ''
      }
    },
    gridTitle() {
      const primaryTitle = `${translate(
        'offDealsFilteredResultsTitle',
        null,
        this.locationOrRegion
      )}`
      const secondaryTitle = translate('offDealsFilteredResults')
      if (!this.filter.isEmpty()) {
        return !this.locationOrRegion ? secondaryTitle : primaryTitle
      }
      return ''
    },
    showGrid() {
      return (
        (this.filter.nonEmpty() && !!this.results.length) ||
        this.isSeeAllFilter ||
        this.emptySliders
      )
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateInnerWidth)
    this.updateInnerWidth()
    this.isMaxLengthStayWithParams()
  },
  unmounted() {
    window.removeEventListener('resize', this.updateInnerWidth)
  },
  async created() {
    await this.getOffers()
    global.$(window).on('auth.logged auth.postlogged auth.nologged', () => {
      this.getOffers()
    })
  },
  methods: {
    ...mapActions(['sendAnalyticsView']),
    ...mapActions('newOffers', [
      'initialize',
      'initializeRevamp',
      'getRegions',
      'getCountriesAndCities',
      'selectCategory',
      'clearFilters',
      'nextPage',
      'updateMoreFilters',
      'seeAll'
    ]),
    handleSeeAll(filters) {
      this.seeAll(filters)
      this.scrollToFilterHeader()
    },
    async getOffers() {
      try {
        this.isLoading = true
        await this.getRegions()
        await this.getCountriesAndCities()
        await this.initialize()
        await this.initializeRevamp()
      } finally {
        this.isLoading = false
        this.sendAnalyticsView()
      }
    },
    toggleMoreFilters(val) {
      this.moreFilterIsOpen = typeof val === 'boolean' ? val : !this.moreFilterIsOpen
    },
    async scrollToFilterHeader() {
      const el = this.$refs.offersRevamp
      if (el) {
        await el.scrollIntoView({ behavior: scrollEffect() })
      }
    },
    handleClearFilters() {
      this.clearFilters()
      this.scrollToFilterHeader()
    },
    updateInnerWidth() {
      this.isMobile = window.innerWidth < media('tablet')
    },
    isMaxLengthStayWithParams () {
      const { startDate, endDate } = getParams('startDate', 'endDate')
      const diffMaxStay = DateFactory.daysBetween(startDate, endDate)
      if (diffMaxStay > MAX_STAY) {
        maxLengthStayErrorModal()
        removeParams('startDate', 'endDate')
      }
    }

  }
}
</script>

<style lang="scss">
.featured-slider-wrapper {
  padding-top: 5rem;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 250ms;
}

.bg-light-gray {
  background-color: color('grey-soft');
}

.filtered-results {
  display: flex;
  justify-content: center;
  align-items: center;
}
.offer-list-enter-active,
.offer-list-leave-active {
  transition: all 1s;
}
.offer-list-enter {
  opacity: 0;
  transform: translateX(50px);
}
.offer-list-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>
